/**
 * Global Publish action: processes Ready for Publishing / Live · Ready → Live and
 * Unpublishing → Unpublished across all publishable collections, writes published_snapshot,
 * writes a publish_logs entry, then triggers one frontend deploy.
 *
 * Mounted in the AppBar (left of the theme toggle) so it is always available.
 */
import React, { useState } from "react";
import { useAuthController, useSnackbarController } from "@firecms/core";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@firecms/ui";
import { getApps } from "firebase/app";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type Firestore,
} from "firebase/firestore";
import {
  CONTENT_STATUS,
  LIVE_READY_STATUS,
  PUBLISH_QUEUE_STATUS,
  PUBLISH_QUEUE_STATUSES,
  UNPUBLISH_QUEUE_STATUS,
} from "../lib/contentStatus";
import { buildPublishedSnapshot } from "../lib/publishedSnapshot";
import { triggerFrontendDeployAfterPublish } from "../lib/triggerFrontendDeploy";

const FIRESTORE_BATCH_LIMIT = 450;

/** Collections included in a site-wide publish run. */
export const PUBLISHABLE_COLLECTIONS = [
  "destinations",
  "guides",
  "articles",
  "authors",
  "site_chrome",
  "page_copy",
  "community_stories",
  "hotels",
  "home_hero_sets",
] as const;

export type PublishableCollection = (typeof PUBLISHABLE_COLLECTIONS)[number];

type QueuedDoc = {
  collectionId: PublishableCollection;
  id: string;
  name: string;
  /** Present for destinations only. */
  level?: string;
  queueStatus?: string;
};

function toLogItem(d: QueuedDoc): Record<string, string> {
  const item: Record<string, string> = {
    collection: d.collectionId,
    id: d.id,
    name: d.name,
  };
  if (d.level) item.level = d.level;
  if (d.queueStatus) item.from_status = d.queueStatus;
  return item;
}

async function queryQueued(
  db: Firestore,
  collectionId: PublishableCollection,
  status: string,
): Promise<QueuedDoc[]> {
  const snap = await getDocs(
    query(collection(db, collectionId), where("status", "==", status)),
  );
  return snap.docs.map((d) => {
    const data = d.data() as Record<string, unknown>;
    const name =
      (typeof data.name === "string" && data.name) ||
      (typeof data.page_key === "string" && data.page_key) ||
      (typeof data.slug === "string" && data.slug) ||
      d.id;
    const level =
      collectionId === "destinations" && typeof data.level === "string"
        ? data.level
        : undefined;
    return { collectionId, id: d.id, name, level, queueStatus: status };
  });
}

async function queryAllPublishQueues(
  db: Firestore,
): Promise<QueuedDoc[]> {
  const out: QueuedDoc[] = [];
  for (const collectionId of PUBLISHABLE_COLLECTIONS) {
    for (const status of PUBLISH_QUEUE_STATUSES) {
      out.push(...(await queryQueued(db, collectionId, status)));
    }
  }
  return out;
}

async function commitPublishUpdates(
  db: Firestore,
  docs: QueuedDoc[],
): Promise<void> {
  for (let i = 0; i < docs.length; i += FIRESTORE_BATCH_LIMIT) {
    const chunk = docs.slice(i, i + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);
    for (const item of chunk) {
      const ref = doc(db, item.collectionId, item.id);
      const snap = await getDoc(ref);
      const data = (snap.data() ?? {}) as Record<string, unknown>;
      const published_snapshot = buildPublishedSnapshot(item.collectionId, data);
      batch.update(ref, {
        status: "published",
        published_snapshot,
        published_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
    }
    await batch.commit();
  }
}

async function commitStatusUpdates(
  db: Firestore,
  docs: QueuedDoc[],
  status: "unpublished",
): Promise<void> {
  for (let i = 0; i < docs.length; i += FIRESTORE_BATCH_LIMIT) {
    const chunk = docs.slice(i, i + FIRESTORE_BATCH_LIMIT);
    const batch = writeBatch(db);
    for (const item of chunk) {
      batch.update(doc(db, item.collectionId, item.id), {
        status,
        updated_at: serverTimestamp(),
      });
    }
    await batch.commit();
  }
}

function summarize(docs: QueuedDoc[]): string {
  if (docs.length === 0) return "none";
  const byCollection = new Map<string, number>();
  for (const d of docs) {
    byCollection.set(d.collectionId, (byCollection.get(d.collectionId) ?? 0) + 1);
  }
  return [...byCollection.entries()]
    .map(([id, count]) => `${id}: ${count}`)
    .join(", ");
}

export function GlobalPublishAction() {
  const snackbar = useSnackbarController();
  const authController = useAuthController();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<{
    toPublish: QueuedDoc[];
    toUnpublish: QueuedDoc[];
  } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadPreview = async () => {
    const app = getApps()[0];
    if (!app) {
      snackbar.open({ type: "error", message: "Firebase is not initialized." });
      return;
    }
    const db = getFirestore(app);
    setPreviewLoading(true);
    try {
      const toPublish = await queryAllPublishQueues(db);
      const toUnpublish: QueuedDoc[] = [];
      for (const collectionId of PUBLISHABLE_COLLECTIONS) {
        toUnpublish.push(...(await queryQueued(db, collectionId, UNPUBLISH_QUEUE_STATUS)));
      }
      setPreview({ toPublish, toUnpublish });
      setOpen(true);
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Failed to load publish queue.",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const writeLog = async (
    db: Firestore,
    logId: string,
    payload: Record<string, unknown>,
  ) => {
    const batch = writeBatch(db);
    batch.set(doc(db, "publish_logs", logId), payload);
    await batch.commit();
  };

  const handlePublish = async () => {
    const app = getApps()[0];
    if (!app) {
      snackbar.open({ type: "error", message: "Firebase is not initialized." });
      return;
    }
    const db = getFirestore(app);
    setLoading(true);

    let toPublish: QueuedDoc[] = [];
    let toUnpublish: QueuedDoc[] = [];
    const startedAt = new Date();
    const logId = doc(collection(db, "publish_logs")).id;
    const triggeredBy =
      (authController.user as { email?: string } | null)?.email ?? null;

    try {
      toPublish = await queryAllPublishQueues(db);
      for (const collectionId of PUBLISHABLE_COLLECTIONS) {
        toUnpublish.push(...(await queryQueued(db, collectionId, UNPUBLISH_QUEUE_STATUS)));
      }

      if (toPublish.length === 0 && toUnpublish.length === 0) {
        snackbar.open({
          type: "info",
          message:
            "Nothing to publish. Set items to Ready for Publishing or Live · Ready first.",
        });
        setOpen(false);
        return;
      }

      await commitPublishUpdates(db, toPublish);
      await commitStatusUpdates(db, toUnpublish, "unpublished");

      await writeLog(db, logId, {
        started_at: startedAt,
        completed_at: serverTimestamp(),
        status: "completed",
        triggered_by: triggeredBy,
        published_count: toPublish.length,
        unpublished_count: toUnpublish.length,
        published: toPublish.map(toLogItem),
        unpublished: toUnpublish.map(toLogItem),
        deploy_ref: import.meta.env.VITE_DEPLOY_GIT_REF?.trim() ?? null,
        error: null,
      });

      snackbar.open({
        type: "success",
        message:
          `Publish complete: ${toPublish.length} published, ${toUnpublish.length} unpublished. ` +
          "Starting frontend deploy…",
      });
      setOpen(false);
      setPreview(null);

      await triggerFrontendDeployAfterPublish(
        () => authController.getAuthToken(),
        snackbar,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "Publish failed.";
      try {
        await writeLog(db, logId, {
          started_at: startedAt,
          completed_at: serverTimestamp(),
          status: "failed",
          triggered_by: triggeredBy,
          published_count: toPublish.length,
          unpublished_count: toUnpublish.length,
          published: toPublish.map(toLogItem),
          unpublished: toUnpublish.map(toLogItem),
          deploy_ref: import.meta.env.VITE_DEPLOY_GIT_REF?.trim() ?? null,
          error: message,
        });
      } catch {
        // ignore log write failure
      }
      snackbar.open({ type: "error", message });
    } finally {
      setLoading(false);
    }
  };

  const publishCount = preview?.toPublish.length ?? 0;
  const unpublishCount = preview?.toUnpublish.length ?? 0;
  const firstPublishCount =
    preview?.toPublish.filter((d) => d.queueStatus === PUBLISH_QUEUE_STATUS).length ?? 0;
  const liveUpdateCount =
    preview?.toPublish.filter((d) => d.queueStatus === LIVE_READY_STATUS).length ?? 0;

  return (
    <>
      <Button
        variant="filled"
        color="primary"
        size="small"
        className="mr-1"
        disabled={previewLoading || loading}
        onClick={() => void loadPreview()}
      >
        {previewLoading ? "Loading…" : "Publish"}
      </Button>
      <Dialog open={open} onOpenChange={(v) => !loading && setOpen(v)}>
        <DialogTitle>Publish site content</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className="mb-3">
            This updates all queued items across collections, stores a live snapshot for
            revert, writes a publish log, then rebuilds the site once.
          </Typography>
          <Typography variant="body2" className="mb-2">
            <strong>
              Publish ({CONTENT_STATUS.ready_for_publishing} → {CONTENT_STATUS.published}):
            </strong>{" "}
            {firstPublishCount}
          </Typography>
          <Typography variant="body2" className="mb-2">
            <strong>
              Update live ({CONTENT_STATUS.published_ready} → {CONTENT_STATUS.published}):
            </strong>{" "}
            {liveUpdateCount}
          </Typography>
          <Typography variant="body2" className="mb-2">
            <strong>Total to publish:</strong> {publishCount} ({summarize(preview?.toPublish ?? [])})
          </Typography>
          <Typography variant="body2">
            <strong>
              Unpublish ({CONTENT_STATUS.unpublishing} → {CONTENT_STATUS.unpublished}):
            </strong>{" "}
            {unpublishCount} ({summarize(preview?.toUnpublish ?? [])})
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" disabled={loading} onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="filled"
            color="primary"
            disabled={loading || (publishCount === 0 && unpublishCount === 0)}
            onClick={() => void handlePublish()}
          >
            {loading ? "Publishing…" : "Confirm Publish"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
